import { useEffect, useMemo, useState } from 'react';
import {
  detectDefaultPhoneCountryInBrowser,
  getMarketConfig,
  type Region,
} from '@court-sync/shared';
import { regionService } from '@/services/regionService';
import type { CountryCode } from 'libphonenumber-js';

export type DefaultPhoneCountrySource = {
  /** ISO 3166-1 alpha-2 country code from academy or profile */
  country?: string | null;
  /** CourtSync region — mapped via MARKET_CONFIG */
  region?: Region | null;
};

/**
 * Resolves default ISO country for phone inputs.
 * Sync-detects from academy context + browser; optionally refines via regionService.
 */
export function useDefaultPhoneCountry(source?: DefaultPhoneCountrySource): CountryCode {
  const syncDefault = useMemo(() => {
    const fromAcademy = source?.country?.trim().toUpperCase();
    const fromRegion = source?.region
      ? getMarketConfig(source.region)?.countryCode
      : undefined;
    return detectDefaultPhoneCountryInBrowser({
      countryCode: fromAcademy || fromRegion,
      region: source?.region ?? undefined,
    });
  }, [source?.country, source?.region]);

  const [country, setCountry] = useState<CountryCode>(syncDefault);

  useEffect(() => {
    setCountry(syncDefault);
  }, [syncDefault]);

  useEffect(() => {
    let cancelled = false;
    void regionService.detectRegion().then((region) => {
      if (cancelled) return;
      const marketCountry = getMarketConfig(region)?.countryCode;
      if (marketCountry && marketCountry !== 'EU') {
        setCountry(detectDefaultPhoneCountryInBrowser({
          countryCode: source?.country ?? marketCountry,
          region: source?.region ?? region,
        }));
      }
    }).catch(() => {
      // Keep sync default
    });
    return () => {
      cancelled = true;
    };
  }, [source?.country, source?.region]);

  return country;
}

export { type CountryCode };
